import classNames from 'classnames'
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { navigateTo } from '../../../../../common/frontend/route-utils.js'
import { localizeHolidayName } from '../../../../../common/holidays.js'
import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { hexBadgeStyle } from '../../../../../common/utils/badge-color.js'
import { formatDisplayDate, formatFte } from '../../../../../common/utils/format-utils.js'
import { getLeaveBadgeEmoji } from '../../../../../common/utils/leave-badge-emoji.js'
import { Button } from '../../../../components/button/button.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import { CloseIcon } from '../../../../components/icon/icon.js'
import { CalendarPlus, ClipboardCheck, Palmtree } from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { buildEmployeeFilterOptions } from '../../../../components/portal-people-filters/people-filter-options.js'
import {
    FilterPopover,
    ViewPopover,
} from '../../../../components/portal-people-filters/portal-people-filters.js'
import { parsePeopleFiltersFromSearch } from '../../../../components/portal-people-filters/types.js'
import type {
    FilterOptions,
    PeopleFilters,
} from '../../../../components/portal-people-filters/types.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { showToast } from '../../../../toast.js'
import type { View } from '../../../../types.js'
import { portalGet, portalPatch } from '../../utils/api.js'
import { computeBookableEmployeeIds } from '../../utils/booking-scope.js'
import {
    formatDateRange,
    formatDateStr,
    formatMonthTag,
    getInitials,
    todayStr,
} from '../../utils/format.js'
import {
    fetchCompanies,
    fetchEmployees,
    fetchLocations,
    fetchManagers,
    fetchTeams,
} from '../../utils/portal-api.js'
import type { TimeAwayManageScope } from '../../utils/portal-api.js'
import type { EmployeeListItem } from '../people/people-view.js'
import './calendar-view.css'
import { resolveCapacityLabel } from './capacity-label.js'

// ── Types ──

interface CalendarEntry {
    id: number
    start_date: string
    end_date: string
    business_days: number
    status: string
    employeeId: number
    firstName: string
    lastName: string
    avatarUrl: string | null
    leaveTypeName: string
    leaveTypeSlug: string
    badgeType: string
    teamNames: string | null
}

interface CompanyHoliday {
    id: number
    date: string
    name: string
    year: number
}

interface WorkingPatternAllocation {
    employeeId: number
    /** JSON string holding a 7-element Mon→Sun array; values are numbers (hours/day) or booleans. */
    workingPattern: string | null
    /** Total hours per week (e.g. 40 for full-time, 20 for half-time). */
    workingHours: number | null
    /** Hours a typical full working day represents (usually 8). */
    standardDayHours: number | null
    /** Fractional FTE, 0..1 (e.g. 0.5 for half-time). */
    fte: number | null
    effectiveFrom: string
    effectiveTo: string | null
}

interface ActiveEmployee {
    employeeId: number
    firstName: string
    lastName: string
    avatarUrl: string | null
    teamNames: string | null
}

interface CalendarApiResponse {
    entries: CalendarEntry[]
    workingPatterns: WorkingPatternAllocation[]
    // The roster is sourced from /api/portal/employees (so it shares the People
    // filter/sort backend); this field is part of the payload but unused here.
    activeEmployees: ActiveEmployee[]
}

interface PendingRequest {
    id: number
    start_date: string
    end_date: string
    business_days: number
    note: string | null
    created_at_utc: string
    employeeId: number
    firstName: string
    lastName: string
    avatarUrl: string | null
    leaveTypeName: string
    badgeType: string
    /** Set on an owner's open deletion request (TASK-402) — the row awaits an
     *  approve/reject-cancellation decision instead of a booking approval. */
    cancel_requested_at_utc: string | null
}

export interface CalendarViewProps {
    view: View
    lang: Language
    myEmployeeId: number | null
    isManager?: boolean
    /** Whose rows the viewer may book time away on by click/drag (TASK-181). */
    timeAwayScope?: TimeAwayManageScope
}

// ── Helpers ──

/** Fallback window length (days) before the container width is measured. */
const DEFAULT_WINDOW_DAYS = 35
/** Comfortable day-column width on desktop — the target spacing in day view. */
const MIN_DAY_WIDTH = 31
/** Tightest a day column may get while still showing a whole month — the threshold
 *  for switching between month view (wide) and day view (smaller). */
const HARD_MIN_DAY_WIDTH = 22
/** Phones can't fit a month legibly — target a few wide columns rather than a cramped
 *  week, paged by day with the arrows. */
const MOBILE_DAY_WIDTH = 42
const MIN_MOBILE_DAYS = 4
/** Width of the left-hand employee/name column (narrower on mobile, where names
 *  truncate, to leave more room for day columns). */
const NAME_COL_WIDTH = 220
const NAME_COL_WIDTH_MOBILE = 180
/** Viewport width at/below which the mobile name-column width applies. */
const MOBILE_BREAKPOINT = 767
/** Fixed width of the trailing column that holds the "next" navigation arrow. */
const ARROW_COL_WIDTH = 36

const monthNames: Record<Language, string[]> = {
    et: [
        'Jaanuar',
        'Veebruar',
        'Märts',
        'Aprill',
        'Mai',
        'Juuni',
        'Juuli',
        'August',
        'September',
        'Oktoober',
        'November',
        'Detsember',
    ],
    en: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ],
}

const dayNames: Record<Language, string[]> = {
    et: ['P', 'E', 'T', 'K', 'N', 'R', 'L'],
    en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
}

/** Build `length` consecutive Date objects starting at `startStr` (YYYY-MM-DD). */
const getWindowDays = (startStr: string, length: number): Date[] => {
    const days: Date[] = []
    const date = new Date(startStr + 'T00:00:00')
    for (let i = 0; i < length; i++) {
        days.push(new Date(date))
        date.setDate(date.getDate() + 1)
    }
    return days
}

/** Shift a YYYY-MM-DD string by `delta` days, returning a YYYY-MM-DD string. */
const shiftDate = (dateStr: string, delta: number): string => {
    const d = new Date(dateStr + 'T00:00:00')
    d.setDate(d.getDate() + delta)
    return formatDateStr(d)
}

/** Shift `dateStr` by `delta` months, returning the 1st of the resulting month (YYYY-MM-DD). */
const addMonths = (dateStr: string, delta: number): string => {
    const d = new Date(dateStr + 'T00:00:00')
    d.setDate(1)
    d.setMonth(d.getMonth() + delta)
    return formatDateStr(d)
}

/** The 1st of the calendar month that `dateStr` falls in (YYYY-MM-DD). */
const startOfMonth = (dateStr: string): string => addMonths(dateStr, 0)

/** Number of days in the calendar month that `dateStr` falls in. */
const daysInMonthOf = (dateStr: string): number => {
    const d = new Date(dateStr + 'T00:00:00')
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
}

/** True when `dateStr` is the 1st of its month. */
const isFirstOfMonth = (dateStr: string): boolean => dateStr.endsWith('-01')

/** Full localized date for a day-header tooltip, e.g. "2. juuli 2026" / "2 July 2026". */
const formatDayTitle = (date: Date, lang: Language): string => {
    const month = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
        month: 'long',
    }).format(date)
    return lang === 'et'
        ? `${date.getDate()}. ${month} ${date.getFullYear()}`
        : `${date.getDate()} ${month} ${date.getFullYear()}`
}

/** Format comma-separated company slugs as uppercase labels, e.g. "play,nope" → "PLAY, NOPE". */
const formatCompanies = (slugs: string): string =>
    slugs
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
        .join(', ')

// View-option persistence — the capacity label and work-pattern overlay toggles
// are remembered per browser so a chosen view sticks across visits.
const CAPACITY_PREF_KEY = 'portal-calendar-show-capacity'
const FLEX_PREF_KEY = 'portal-calendar-show-flex'

const readStoredBool = (key: string, fallback: boolean): boolean => {
    try {
        const stored = localStorage.getItem(key)
        return stored === null ? fallback : stored === 'true'
    } catch {
        return fallback
    }
}

const writeStoredBool = (key: string, value: boolean): void => {
    try {
        localStorage.setItem(key, value ? 'true' : 'false')
    } catch {
        // ignore — private mode / disabled storage
    }
}

// ── Component ──

export const CalendarView = ({
    view,
    lang,
    myEmployeeId,
    isManager = false,
    timeAwayScope = null,
}: CalendarViewProps) => {
    // Anchored on today (no horizontal scroll). On wide screens this renders as the
    // whole calendar month containing today (month view); on smaller screens as a
    // today-anchored sliding window of day columns (day view) — see `monthMode` below.
    const [windowStart, setWindowStart] = useState(todayStr())
    const [refreshKey, setRefreshKey] = useState(0)
    const [entries, setEntries] = useState<CalendarEntry[]>([])
    const [workingPatternAllocations, setWorkingPatternAllocations] = useState<
        WorkingPatternAllocation[]
    >([])
    const [holidays, setHolidays] = useState<CompanyHoliday[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // View options (remembered per browser): the capacity label next to the
    // company name, and the work-pattern (flex / part-time) cell overlay.
    const [showCapacity, setShowCapacity] = useState(() => readStoredBool(CAPACITY_PREF_KEY, true))
    const [showFlex, setShowFlex] = useState(() => readStoredBool(FLEX_PREF_KEY, true))
    useEffect(() => writeStoredBool(CAPACITY_PREF_KEY, showCapacity), [showCapacity])
    useEffect(() => writeStoredBool(FLEX_PREF_KEY, showFlex), [showFlex])

    // How many days the timeline shows — derived from the container width so the
    // grid always fills the screen exactly (no horizontal scroll).
    const ganttRef = useRef<HTMLDivElement>(null)
    const [visibleDays, setVisibleDays] = useState(DEFAULT_WINDOW_DAYS)
    const [nameColWidth, setNameColWidth] = useState(NAME_COL_WIDTH)

    // Roster (filtered + sorted server-side via the People endpoint)
    const [roster, setRoster] = useState<EmployeeListItem[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    // Seed filters from the URL so links like `/calendar?teamId=42` (e.g. the
    // "Tiimi hõivatus" card's "View all") land pre-filtered to that team.
    const [filters, setFilters] = useState<PeopleFilters>(() =>
        parsePeopleFiltersFromSearch(window.location.search),
    )
    // Reference data for the Filter popover
    const [companies, setCompanies] = useState<{ id: number; name: string }[]>([])
    const [teams, setTeams] = useState<{ id: number; name: string }[]>([])
    const [locations, setLocations] = useState<
        { id: number; name: string; country_code: string }[]
    >([])
    const [managers, setManagers] = useState<
        { id: number; first_name: string; last_name: string }[]
    >([])

    // Pending approvals state
    const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([])
    const [showPending, setShowPending] = useState(false)
    const [actionLoading, setActionLoading] = useState<number | null>(null)
    const [rejectingId, setRejectingId] = useState<number | null>(null)
    const [rejectReason, setRejectReason] = useState('')

    // Slide-over state
    const [selectedEntry, setSelectedEntry] = useState<CalendarEntry | null>(null)

    // Close the pending-approvals modal with ESC, like every other modal.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowPending(false)
            }
        }
        if (showPending) {
            window.addEventListener('keydown', handleKeyDown)
        }
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [showPending])

    // Shared hover tooltip (leave pills + holiday dots). Rendered fixed-position
    // at the .portal-calendar root so it escapes the gantt's overflow clipping.
    const [hoverTip, setHoverTip] = useState<{ rect: DOMRect; content: ReactNode } | null>(null)

    // Column hover — highlights a whole day column and anchors an away-summary tooltip.
    const [hoveredColIdx, setHoveredColIdx] = useState<number | null>(null)
    const headerCellRefs = useRef<(HTMLElement | null)[]>([])

    // Click-drag booking on a bookable row (TASK-181, Humaans-style): mousedown
    // on a day cell anchors a selection, the head follows the pointer's column,
    // and mouseup opens the booking modal prefilled with the range. A plain
    // click books a single day. Escape cancels an in-flight drag.
    const [dragSel, setDragSel] = useState<{
        empId: number
        anchorIdx: number
        headIdx: number
    } | null>(null)

    const todayDate = todayStr()

    // Month view kicks in once a whole month fits (≥31 columns). It always renders the
    // full calendar month that `windowStart` falls in (1st → last day); day view renders
    // a `visibleDays`-long window starting at `windowStart`. `pageStart`/`windowLength`
    // are the actual rendered range and drive both the grid and the data fetch.
    const monthMode = visibleDays >= 31
    const pageStart = monthMode ? startOfMonth(windowStart) : windowStart
    const windowLength = monthMode ? daysInMonthOf(windowStart) : visibleDays

    const days = getWindowDays(pageStart, windowLength)
    const holidaySet = new Set(holidays.map((h) => h.date))

    // Column indices that start a new month (skip the first column — a divider
    // before the window's opening day is meaningless). Used to draw the dashed
    // month-boundary lines across the gantt.
    const monthBoundaryIdxs = days
        .map((day, idx) => (idx > 0 && isFirstOfMonth(formatDateStr(day)) ? idx : -1))
        .filter((idx) => idx >= 0)

    // Measure the gantt and recompute how many day columns fit on resize.
    useEffect(() => {
        const el = ganttRef.current
        if (!el) return
        const compute = () => {
            const width = el.clientWidth
            const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
            const nameCol = isMobile ? NAME_COL_WIDTH_MOBILE : NAME_COL_WIDTH
            setNameColWidth(nameCol)
            const usable = width - nameCol - ARROW_COL_WIDTH
            if (isMobile) {
                // Phones never fit a month — a few wide, legible day columns, paged by day.
                setVisibleDays(Math.max(MIN_MOBILE_DAYS, Math.floor(usable / MOBILE_DAY_WIDTH)))
                return
            }
            // Desktop: if a whole 31-day month fits (even at the tight HARD_MIN width)
            // switch to month view (≥31 flips `monthMode`); otherwise show as many
            // comfortable day columns as fit.
            const maxThatFits = Math.floor(usable / HARD_MIN_DAY_WIDTH)
            if (maxThatFits >= 31) {
                setVisibleDays(31)
                return
            }
            setVisibleDays(Math.max(7, Math.floor(usable / MIN_DAY_WIDTH)))
        }
        compute()
        const ro = new ResizeObserver(compute)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // For each allocation, pre-compute a 7-element Mon→Sun hours-per-day array.
    // Numeric pattern entries are the hours directly. Boolean entries mean
    // "is this a working day?" — actual hours come from
    // `workingHours / count(true days)` (e.g. Mon-Fri at 20h/wk = 4h/day).
    const workingPatternByEmployee = (() => {
        const byEmp = new Map<
            number,
            { from: string; to: string | null; hoursPerDay: number[]; standardDayHours: number }[]
        >()
        for (const alloc of workingPatternAllocations) {
            let parsed: (number | boolean)[] | null = null
            try {
                const raw: unknown = alloc.workingPattern ? JSON.parse(alloc.workingPattern) : null
                if (
                    Array.isArray(raw) &&
                    raw.length === 7 &&
                    raw.every((v) => typeof v === 'number' || typeof v === 'boolean')
                ) {
                    parsed = raw
                }
            } catch {
                parsed = null
            }
            if (!parsed) continue
            const standardDay = alloc.standardDayHours ?? 8
            const trueCount = parsed.filter((v) => v === true).length
            const hoursForTrue =
                trueCount > 0 && alloc.workingHours !== null
                    ? alloc.workingHours / trueCount
                    : standardDay
            const hoursPerDay = parsed.map((v) => {
                if (typeof v === 'number') return v
                return v ? hoursForTrue : 0
            })
            const list = byEmp.get(alloc.employeeId) ?? []
            list.push({
                from: alloc.effectiveFrom,
                to: alloc.effectiveTo,
                hoursPerDay,
                standardDayHours: standardDay,
            })
            byEmp.set(alloc.employeeId, list)
        }
        return byEmp
    })()

    type WorkingStatus = 'full' | 'partial' | 'off'

    /** Classifies the employee's working status for a given date. */
    const workingDayStatus = (
        employeeId: number,
        dateStr: string,
        jsDay: number,
    ): WorkingStatus => {
        const allocs = workingPatternByEmployee.get(employeeId)
        if (!allocs) return 'full'
        // Pick the latest allocation that covers this date (descending from).
        const covering = allocs
            .filter((a) => a.from <= dateStr && (a.to === null || a.to >= dateStr))
            .sort((a, b) => b.from.localeCompare(a.from))
        const active = covering[0]
        if (!active) return 'full'
        // JS getDay(): 0=Sun, 1=Mon, …, 6=Sat. Pattern is Mon=0 … Sun=6.
        const patternIdx = (jsDay + 6) % 7
        const hours = active.hoursPerDay[patternIdx]
        if (hours === 0) return 'off'
        if (hours < active.standardDayHours) return 'partial'
        return 'full'
    }

    // FTE per employee from the working-pattern allocation active today. Only
    // the FTE is read here — whether someone is FLEX comes from their contract
    // form (roster `collaboration_type`), never from the pattern's shape; see
    // `resolveCapacityLabel` (TASK-535).
    const activeFteByEmployee = useMemo(() => {
        const byEmp = new Map<number, WorkingPatternAllocation[]>()
        for (const alloc of workingPatternAllocations) {
            const list = byEmp.get(alloc.employeeId) ?? []
            list.push(alloc)
            byEmp.set(alloc.employeeId, list)
        }
        const result = new Map<number, number | null>()
        for (const [empId, allocs] of byEmp) {
            const sorted = [...allocs].sort((a, b) =>
                b.effectiveFrom.localeCompare(a.effectiveFrom),
            )
            const active =
                sorted.find(
                    (a) =>
                        a.effectiveFrom <= todayDate &&
                        (a.effectiveTo === null || a.effectiveTo >= todayDate),
                ) ?? sorted[0]
            if (active) result.set(empId, active.fte)
        }
        return result
    }, [workingPatternAllocations, todayDate])

    // Load window-scoped data (entries, working patterns, holidays, balances, pending)
    useEffect(() => {
        const load = async () => {
            setIsLoading(true)
            const winDays = getWindowDays(pageStart, windowLength)
            const startDate = formatDateStr(winDays[0])
            const endDate = formatDateStr(winDays[winDays.length - 1])
            const years = [
                ...new Set([
                    new Date(startDate + 'T00:00:00').getFullYear(),
                    new Date(endDate + 'T00:00:00').getFullYear(),
                ]),
            ]

            const [calResult, ...holResults] = await Promise.all([
                portalGet<CalendarApiResponse>(
                    `/api/portal/time-off/calendar?start=${startDate}&end=${endDate}`,
                ),
                ...years.map((y) =>
                    portalGet<CompanyHoliday[]>(`/api/portal/company-holidays?year=${y}`),
                ),
            ])

            if (calResult.ok) {
                setEntries(calResult.data.entries)
                setWorkingPatternAllocations(calResult.data.workingPatterns)
            }
            const hols: CompanyHoliday[] = []
            for (const r of holResults) if (r.ok) hols.push(...r.data)
            setHolidays(hols)

            if (isManager) {
                const pendingResult = await portalGet<PendingRequest[]>(
                    '/api/portal/time-off/pending',
                )
                if (pendingResult.ok) setPendingRequests(pendingResult.data)
            }

            setIsLoading(false)
        }

        void load()
    }, [pageStart, windowLength, refreshKey, isManager])

    // Load the roster — reuses the People endpoint so filter/sort semantics match.
    // Every active employee is shown by default; rows are only removed when a
    // filter/search is explicitly set. The endpoint caps a page at 100, so we
    // page through until the full set is collected (no silent truncation).
    useEffect(() => {
        void (async () => {
            const PAGE_SIZE = 100
            const all: EmployeeListItem[] = []
            for (let page = 1; ; page++) {
                const result = await fetchEmployees({
                    search: searchQuery,
                    sort: 'last_name',
                    dir: 'asc',
                    companyId: filters.companyId,
                    teamId: filters.teamId,
                    locationId: filters.locationId,
                    countryCode: filters.countryCode,
                    managerId: filters.managerId,
                    status: 'active',
                    // Only show people active today — hide not-yet-onboarded and departed.
                    currentlyActive: true,
                    limit: PAGE_SIZE,
                    page,
                })
                if (!result.ok) break
                all.push(...result.data)
                if (all.length >= result.total || result.data.length === 0) break
            }
            setRoster(all)
        })()
    }, [searchQuery, filters])

    // Load reference data for the Filter popover + booking policies (once)
    useEffect(() => {
        void (async () => {
            const [compRes, teamRes, locRes, mgrRes] = await Promise.all([
                fetchCompanies(),
                // The roster below only shows currently active people, so the
                // team/manager filters must not offer teams with no active
                // members or managers who are no longer active.
                fetchTeams({ currentlyActive: true }),
                fetchLocations(),
                fetchManagers({ currentlyActive: true }),
            ])
            if (compRes.ok) setCompanies(compRes.data)
            if (teamRes.ok) setTeams(teamRes.data)
            if (locRes.ok) setLocations(locRes.data)
            if (mgrRes.ok) setManagers(mgrRes.data)
        })()
    }, [])

    const filterOptions: FilterOptions = useMemo(
        () => buildEmployeeFilterOptions(lang, { companies, teams, locations, managers }),
        [companies, teams, locations, managers, lang],
    )

    // Group leave entries by employee so each roster row can pull its own pills.
    const entriesByEmployee = new Map<number, CalendarEntry[]>()
    for (const entry of entries) {
        const list = entriesByEmployee.get(entry.employeeId) ?? []
        list.push(entry)
        entriesByEmployee.set(entry.employeeId, list)
    }

    const employeeRows: [
        number,
        {
            firstName: string
            lastName: string
            avatarUrl: string | null
            teamNames: string | null
            companySlugs: string
            collaborationType: string | null
            entries: CalendarEntry[]
        },
    ][] = roster.map((emp) => [
        emp.id,
        {
            firstName: emp.first_name,
            lastName: emp.last_name,
            avatarUrl: emp.avatar_url,
            teamNames: emp.team_names,
            companySlugs: emp.company_slugs,
            collaborationType: emp.collaboration_type,
            entries: entriesByEmployee.get(emp.id) ?? [],
        },
    ])

    // When sort === 'leave_type' (Humaans-style), order people by their leave
    // status relative to today:
    //   1. Currently away today — grouped by leave type, earliest start first
    //   2. Upcoming leave — grouped by leave type, earliest start first
    //   3. No leave in the window — keep incoming alphabetical order
    //
    // The default last_name sort still applies the original behaviour: people
    // with leave bubble up (earliest start first), rest keep alphabetical order.
    const isDefaultView = searchQuery.trim() === '' && Object.keys(filters).length === 0
    const earliestLeaveStart = (es: CalendarEntry[]): string | null =>
        es.length === 0
            ? null
            : es.reduce((min, e) => (e.start_date < min ? e.start_date : min), es[0].start_date)

    /** 0 = away today, 1 = upcoming, 2 = no leave */
    const leaveTier = (es: CalendarEntry[], today: string): 0 | 1 | 2 => {
        if (es.length === 0) return 2
        for (const e of es) {
            if (e.start_date <= today && e.end_date >= today) return 0
        }
        return 1
    }

    const primaryLeaveType = (es: CalendarEntry[]): string =>
        es.reduce((earliest, e) => (e.start_date < earliest.start_date ? e : earliest), es[0])
            .leaveTypeName

    const orderedRows = isDefaultView
        ? [...employeeRows].sort(([_aId, a], [_bId, b]) => {
              const today = todayDate
              const aTier = leaveTier(a.entries, today)
              const bTier = leaveTier(b.entries, today)
              if (aTier !== bTier) return aTier - bTier
              if (aTier === 2) return 0
              const typeCmp = primaryLeaveType(a.entries).localeCompare(primaryLeaveType(b.entries))
              if (typeCmp !== 0) return typeCmp
              return (earliestLeaveStart(a.entries) ?? '').localeCompare(
                  earliestLeaveStart(b.entries) ?? '',
              )
          })
        : employeeRows

    // Legend — distinct leave types present in the visible window (per-type colors).
    const legendTypes: { badgeType: string; name: string }[] = []
    const seenBadge = new Set<string>()
    for (const entry of entries) {
        if (seenBadge.has(entry.badgeType)) continue
        seenBadge.add(entry.badgeType)
        legendTypes.push({ badgeType: entry.badgeType, name: entry.leaveTypeName })
    }

    // Label the header with the month that dominates the window (its middle day), so a
    // window spanning two months reads as the month you're mostly looking at — not the
    // first column's month. Per-column month tags + day tooltips disambiguate the rest.
    const labelDate = days[Math.floor(days.length / 2)]
    const windowLabel = `${monthNames[lang][labelDate.getMonth()]} ${labelDate.getFullYear()}`
    // Month view steps whole calendar months; day view shifts the window by its full
    // width (contiguous, so no day is skipped between pages). The label jumps to today —
    // i.e. the current month in month view, a today-anchored window in day view.
    const goPrev = () =>
        setWindowStart((s) => (monthMode ? addMonths(s, -1) : shiftDate(s, -visibleDays)))
    const goNext = () =>
        setWindowStart((s) => (monthMode ? addMonths(s, 1) : shiftDate(s, visibleDays)))
    const goToday = () => setWindowStart(todayStr())

    // Grid columns: 1 name col + N day cols that share the remaining width equally
    // (no horizontal scroll — the day count is sized to fit the container).
    const gridTemplateColumns = `${nameColWidth}px repeat(${days.length}, 1fr) ${ARROW_COL_WIDTH}px`

    // Rows the viewer may book on: self always, plus reports/everyone per scope.
    const bookableIds = computeBookableEmployeeIds(roster, myEmployeeId, timeAwayScope)

    // Commit or cancel the drag selection. Mouseup anywhere commits (the head
    // keeps the last hovered column, so releasing outside the grid still books
    // the visible selection); Escape cancels.
    useEffect(() => {
        if (!dragSel) return
        const commit = () => {
            setDragSel(null)
            const startIdx = Math.min(dragSel.anchorIdx, dragSel.headIdx)
            const endIdx = Math.max(dragSel.anchorIdx, dragSel.headIdx)
            const emp = roster.find((e) => e.id === dragSel.empId)
            view.state._openLeaveBooking?.({
                onSuccess: () => setRefreshKey((k) => k + 1),
                targetEmployee:
                    dragSel.empId !== myEmployeeId && emp
                        ? { id: emp.id, name: `${emp.first_name} ${emp.last_name}` }
                        : undefined,
                initialRange: {
                    start: formatDateStr(days[startIdx]),
                    end: formatDateStr(days[endIdx]),
                },
            })
        }
        const cancelOnEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDragSel(null)
        }
        window.addEventListener('mouseup', commit)
        window.addEventListener('keydown', cancelOnEscape)
        return () => {
            window.removeEventListener('mouseup', commit)
            window.removeEventListener('keydown', cancelOnEscape)
        }
    })

    // Calculate leave bar position using direct day index (no time arithmetic).
    // `isSingleDay` is true when the entry occupies a single visible cell — those
    // pills show only the centered emoji (the label has no room).
    const getBarLayout = (entry: CalendarEntry): { style: CSSProperties; isSingleDay: boolean } => {
        const entryStart = entry.start_date // 'YYYY-MM-DD'
        const entryEnd = entry.end_date

        // Find first and last visible day indices in the window
        const firstDayStr = formatDateStr(days[0])
        const lastDayStr = formatDateStr(days[days.length - 1])

        // Clamp to visible window range
        const clampedStart = entryStart < firstDayStr ? firstDayStr : entryStart
        const clampedEnd = entryEnd > lastDayStr ? lastDayStr : entryEnd

        // Find index positions in the days array
        let startIdx = -1
        let endIdx = -1
        for (let i = 0; i < days.length; i++) {
            const ds = formatDateStr(days[i])
            if (ds === clampedStart) startIdx = i
            if (ds === clampedEnd) endIdx = i
        }

        // Fallback: if entry isn't visible in this window, hide it
        if (startIdx === -1 || endIdx === -1) {
            return { style: { display: 'none' }, isSingleDay: false }
        }

        // CSS grid columns: col 1 = name, col 2 = day[0], col 3 = day[1], etc.
        const startCol = startIdx + 2
        const endCol = endIdx + 3 // +3 to span through the end day cell

        return {
            style: { gridColumn: `${startCol} / ${endCol}` },
            isSingleDay: startIdx === endIdx,
        }
    }

    // Handle approve/reject
    const handleApproval = async (requestId: number, action: 'approve' | 'reject') => {
        setActionLoading(requestId)
        const body: Record<string, string> = { action }
        if (action === 'reject' && rejectReason) {
            body.rejection_reason = rejectReason
        }

        const result = await portalPatch(`/api/portal/time-off/requests/${requestId}`, body)

        if (result.ok) {
            setRejectingId(null)
            setRejectReason('')
            setRefreshKey((k) => k + 1)
        } else {
            showToast(result.error, 'error')
        }
        setActionLoading(null)
    }

    // Decide an owner's deletion request (TASK-402): approve removes the
    // absence and refunds the days, reject keeps it in place.
    const handleCancellationDecision = async (requestId: number, action: 'approve' | 'reject') => {
        setActionLoading(requestId)
        const result = await portalPatch(`/api/portal/time-off/requests/${requestId}`, {
            action: action === 'approve' ? 'approve_cancel' : 'reject_cancel',
        })
        if (result.ok) {
            setRefreshKey((k) => k + 1)
        } else {
            showToast(result.error, 'error')
        }
        setActionLoading(null)
    }

    // Holidays within the visible window
    const windowStartStr = formatDateStr(days[0])
    const windowEndStr = formatDateStr(days[days.length - 1])
    const windowHolidays = holidays
        .filter((h) => h.date >= windowStartStr && h.date <= windowEndStr)
        .sort((a, b) => a.date.localeCompare(b.date))

    const tOff = t.portal.timeOff

    // Hover tooltip helpers — anchor to the hovered element's viewport rect.
    const showTip = (target: HTMLElement, content: ReactNode) => {
        setHoverTip({ rect: target.getBoundingClientRect(), content })
    }
    const hideTip = () => setHoverTip(null)

    const renderLeaveTip = (entry: CalendarEntry): ReactNode => (
        <>
            <div className="portal-calendar__hover-tip-title">
                <span>{getLeaveBadgeEmoji(entry.badgeType)}</span>
                <span>{entry.leaveTypeName}</span>
            </div>
            <div className="portal-calendar__hover-tip-row">
                {formatDateRange(entry.start_date, entry.end_date, lang)}
            </div>
            <div className="portal-calendar__hover-tip-row">
                {tOff.businessDays(entry.business_days)(lang)}
            </div>
            <div className="portal-calendar__hover-tip-row">
                {tOff.status[entry.status.toLowerCase() as 'approved' | 'pending' | 'rejected'](
                    lang,
                )}
            </div>
        </>
    )

    const renderHolidayTip = (dateStr: string): ReactNode => {
        const holiday = holidays.find((h) => h.date === dateStr)
        const name = holiday ? localizeHolidayName(holiday.date, holiday.name, lang) : null
        // Day + month name, no year (e.g. "23. juuni" / "23 June").
        const date = new Date(dateStr + 'T00:00:00')
        const month = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
            month: 'long',
        }).format(date)
        const dayMonth =
            lang === 'et' ? `${date.getDate()}. ${month}` : `${date.getDate()} ${month}`
        const label = tOff.legendPublicHoliday(lang)
        return (
            <>
                <div className="portal-calendar__hover-tip-title">
                    {name ? `${label} - ${name}` : label}
                </div>
                <div className="portal-calendar__hover-tip-row">{dayMonth}</div>
            </>
        )
    }

    // Summary of who's away on a given day column, grouped by leave type name.
    // Returns null when nobody is away (so the column tints without a tooltip).
    const renderColumnSummary = (idx: number): ReactNode => {
        const dateStr = formatDateStr(days[idx])
        const byType = new Map<string, number>()
        let peopleAway = 0
        for (const [, employee] of employeeRows) {
            const active = employee.entries.filter(
                (e) => e.start_date <= dateStr && e.end_date >= dateStr,
            )
            if (active.length === 0) continue
            peopleAway++
            for (const entry of active) {
                byType.set(entry.leaveTypeName, (byType.get(entry.leaveTypeName) ?? 0) + 1)
            }
        }
        if (peopleAway === 0) return null
        return (
            <>
                <div className="portal-calendar__hover-tip-title">
                    {tOff.membersAway(peopleAway)(lang)}
                </div>
                {[...byType.entries()].map(([typeName, count]) => (
                    <div key={typeName} className="portal-calendar__hover-tip-row">
                        {count} {typeName}
                    </div>
                ))}
            </>
        )
    }

    const handleColEnter = (idx: number) => {
        setHoveredColIdx(idx)
        const summary = renderColumnSummary(idx)
        const anchor = headerCellRefs.current[idx]
        if (summary && anchor) showTip(anchor, summary)
        else hideTip()
    }

    return (
        <PageShell className="portal-calendar-shell">
            {/* Header — title + search (and the pending-approvals icon for managers) */}
            <PageHeader
                title={tOff.calendarTitle(lang)}
                actions={
                    <div className="portal-calendar__header-actions">
                        <TextField
                            className="portal-calendar__search"
                            size="small"
                            placeholder={tOff.filter.search(lang)}
                            value={searchQuery}
                            onChange={(value) => setSearchQuery(value)}
                        />
                        {isManager && pendingRequests.length > 0 && (
                            <button
                                type="button"
                                className="portal-calendar__pending-icon"
                                onClick={() => setShowPending(true)}
                                aria-label={tOff.pendingApprovals(lang)}
                                title={tOff.pendingApprovals(lang)}
                            >
                                <ClipboardCheck size={18} />
                                <span className="portal-calendar__pending-icon-badge">
                                    {pendingRequests.length}
                                </span>
                            </button>
                        )}
                    </div>
                }
            />

            {/* Pending Approvals Modal (Manager/Admin only — opened via header icon) */}
            {isManager && showPending && pendingRequests.length > 0 && (
                <div
                    className="portal-calendar__modal-overlay"
                    onClick={() => setShowPending(false)}
                >
                    <div
                        className="portal-calendar__modal portal-calendar__modal--pending"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="portal-calendar__modal-header">
                            <h3 className="portal-calendar__modal-title">
                                {tOff.pendingApprovals(lang)}
                            </h3>
                            <button
                                type="button"
                                className="portal-calendar__slideover-close"
                                onClick={() => setShowPending(false)}
                                aria-label={t.common.close(lang)}
                            >
                                <CloseIcon size={18} />
                            </button>
                        </div>

                        <div className="portal-calendar__pending-list">
                            {pendingRequests.map((req) => (
                                <div key={req.id} className="portal-calendar__pending-card">
                                    <div className="portal-calendar__pending-info">
                                        <div className="portal-calendar__pending-employee">
                                            <div className="portal-calendar__avatar">
                                                {req.avatarUrl ? (
                                                    <img src={req.avatarUrl} alt="" />
                                                ) : (
                                                    getInitials(req.firstName, req.lastName)
                                                )}
                                            </div>
                                            <div>
                                                <div className="portal-calendar__pending-name">
                                                    {req.firstName} {req.lastName}
                                                </div>
                                                <div className="portal-calendar__pending-type">
                                                    {req.leaveTypeName}
                                                    {req.cancel_requested_at_utc !== null &&
                                                        ` — ${tOff.status.cancelRequested(lang)}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="portal-calendar__pending-dates">
                                            {formatDisplayDate(req.start_date)} →{' '}
                                            {formatDisplayDate(req.end_date)}
                                            <span className="portal-calendar__pending-days">
                                                ({tOff.businessDays(req.business_days)(lang)})
                                            </span>
                                        </div>
                                        {req.note && (
                                            <div className="portal-calendar__pending-note">
                                                {req.note}
                                            </div>
                                        )}
                                    </div>

                                    {req.cancel_requested_at_utc !== null ? (
                                        // An open deletion request — approving
                                        // removes the taken absence and refunds
                                        // the days; rejecting keeps it.
                                        <div className="portal-calendar__pending-actions">
                                            <Button
                                                size="small"
                                                variant="alert"
                                                text={tOff.approveCancellation(lang)}
                                                isDisabled={actionLoading === req.id}
                                                onClick={() =>
                                                    void handleCancellationDecision(
                                                        req.id,
                                                        'approve',
                                                    )
                                                }
                                            />
                                            <Button
                                                size="small"
                                                variant="tertiary"
                                                text={tOff.rejectCancellation(lang)}
                                                isDisabled={actionLoading === req.id}
                                                onClick={() =>
                                                    void handleCancellationDecision(
                                                        req.id,
                                                        'reject',
                                                    )
                                                }
                                            />
                                        </div>
                                    ) : rejectingId === req.id ? (
                                        <div className="portal-calendar__pending-reject-form">
                                            <TextField
                                                as="textarea"
                                                className="portal-calendar__form-textarea"
                                                value={rejectReason}
                                                onChange={(value) => setRejectReason(value)}
                                                placeholder={tOff.rejectionReason(lang)}
                                                textAreaProps={{ rows: 2 }}
                                            />
                                            <div className="portal-calendar__pending-actions">
                                                <Button
                                                    size="small"
                                                    variant="tertiary"
                                                    text={tOff.cancel(lang)}
                                                    onClick={() => {
                                                        setRejectingId(null)
                                                        setRejectReason('')
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant="alert"
                                                    text={tOff.reject(lang)}
                                                    isDisabled={actionLoading === req.id}
                                                    onClick={() =>
                                                        void handleApproval(req.id, 'reject')
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="portal-calendar__pending-actions">
                                            <Button
                                                size="small"
                                                variant="success"
                                                text={tOff.approve(lang)}
                                                isDisabled={actionLoading === req.id}
                                                onClick={() =>
                                                    void handleApproval(req.id, 'approve')
                                                }
                                            />
                                            <Button
                                                size="small"
                                                variant="alert"
                                                text={tOff.reject(lang)}
                                                onClick={() => setRejectingId(req.id)}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Toolbar — legend on the left, Filter/Sort/Book on the right */}
            <div className="portal-calendar__toolbar">
                <div className="portal-calendar__legend">
                    {legendTypes.map((lt) => {
                        const hex = hexBadgeStyle(lt.badgeType)
                        return (
                            <span key={lt.badgeType} className="portal-calendar__legend-item">
                                <span
                                    className={classNames(
                                        'portal-calendar__legend-dot',
                                        !hex && `portal-calendar__leave-bar--${lt.badgeType}`,
                                    )}
                                    style={hex ?? undefined}
                                />
                                <span className="portal-calendar__legend-emoji">
                                    {getLeaveBadgeEmoji(lt.badgeType)}
                                </span>
                                {lt.name}
                            </span>
                        )
                    })}
                    <span className="portal-calendar__legend-item">
                        <span className="portal-calendar__legend-dot portal-calendar__legend-dot--holiday" />
                        {tOff.legendPublicHoliday(lang)}
                    </span>
                </div>
                <div className="portal-calendar__toolbar-controls">
                    <ViewPopover
                        label={tOff.viewBtn(lang)}
                        toggles={[
                            {
                                key: 'capacity',
                                label: tOff.viewCapacity(lang),
                                active: showCapacity,
                                onToggle: () => setShowCapacity((v) => !v),
                            },
                            {
                                key: 'flex',
                                label: tOff.viewFlex(lang),
                                active: showFlex,
                                onToggle: () => setShowFlex((v) => !v),
                            },
                        ]}
                    />
                    <FilterPopover
                        lang={lang}
                        filters={filters}
                        options={filterOptions}
                        onChange={setFilters}
                    />
                    {myEmployeeId && (
                        <Button
                            size="small"
                            variant="primary"
                            iconLeft={CalendarPlus}
                            text={tOff.bookTimeAway(lang)}
                            onClick={() =>
                                view.state._openLeaveBooking?.({
                                    onSuccess: () => setRefreshKey((k) => k + 1),
                                })
                            }
                        />
                    )}
                </div>
            </div>

            {/* Gantt chart */}
            <div
                ref={ganttRef}
                className={classNames(
                    'portal-calendar__gantt',
                    isLoading && 'portal-calendar__gantt--loading',
                    dragSel && 'portal-calendar__gantt--dragging',
                )}
                onScroll={hideTip}
                onMouseLeave={() => {
                    setHoveredColIdx(null)
                    hideTip()
                }}
            >
                {isLoading && (
                    <div className="portal-calendar__gantt-loading">
                        <Spinner />
                    </div>
                )}
                <div className="portal-calendar__gantt-scroll">
                    {/* Header — the name column holds the month label + prev arrow; the
                        next arrow sits at the far right of the header row. */}
                    <div className="portal-calendar__gantt-header" style={{ gridTemplateColumns }}>
                        <div className="portal-calendar__name-col portal-calendar__name-col--nav">
                            <button
                                type="button"
                                className="portal-calendar__nav-label"
                                onClick={goToday}
                                title={t.portal.inline.today(lang)}
                            >
                                {windowLabel}
                            </button>
                            <button
                                type="button"
                                className="portal-calendar__nav-arrow"
                                onClick={goPrev}
                                aria-label={t.common.previous(lang)}
                            >
                                ←
                            </button>
                        </div>
                        {days.map((day, idx) => {
                            const dateStr = formatDateStr(day)
                            const dayOfWeek = day.getDay()
                            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
                            const isHoliday = holidaySet.has(dateStr)
                            const isToday = dateStr === todayDate
                            // First day of a new month within the window — flagged so a
                            // window that spans two months stays unambiguous (skip idx 0,
                            // which is just the window's opening day).
                            const isMonthStart = idx > 0 && day.getDate() === 1

                            return (
                                <div
                                    key={dateStr}
                                    ref={(el) => {
                                        headerCellRefs.current[idx] = el
                                    }}
                                    className={classNames(
                                        'portal-calendar__gantt-header-cell',
                                        isWeekend && 'is-weekend',
                                        isHoliday && 'is-holiday',
                                        isToday && 'is-today',
                                        idx === hoveredColIdx && 'is-col-hovered',
                                    )}
                                    title={formatDayTitle(day, lang)}
                                    onMouseEnter={() => {
                                        if (isHoliday) {
                                            setHoveredColIdx(idx)
                                            const anchor = headerCellRefs.current[idx]
                                            if (anchor) {
                                                showTip(anchor, renderHolidayTip(dateStr))
                                            }
                                        } else {
                                            handleColEnter(idx)
                                        }
                                    }}
                                    onMouseLeave={hideTip}
                                >
                                    <div className="portal-calendar__day-name">
                                        {isMonthStart ? (
                                            <span className="portal-calendar__month-tag">
                                                {formatMonthTag(day, lang)}
                                            </span>
                                        ) : (
                                            dayNames[lang][dayOfWeek]
                                        )}
                                    </div>
                                    <div className="portal-calendar__day-num">{day.getDate()}</div>
                                </div>
                            )
                        })}
                        <button
                            type="button"
                            className="portal-calendar__nav-arrow portal-calendar__nav-arrow--next"
                            onClick={goNext}
                            aria-label={t.common.next(lang)}
                        >
                            →
                        </button>
                    </div>

                    {/* Rows */}
                    {orderedRows.length === 0 ? (
                        isLoading ? null : (
                            <div className="portal-calendar__empty">
                                <div className="portal-calendar__empty-icon">
                                    <Palmtree size={32} />
                                </div>
                                <div className="portal-calendar__empty-text">
                                    {tOff.noRequests(lang)}
                                </div>
                            </div>
                        )
                    ) : (
                        orderedRows.map(([empId, employee]) => {
                            const rowBookable = bookableIds.has(empId)
                            // Selection bounds while dragging on this row.
                            const rowSel = dragSel?.empId === empId ? dragSel : null
                            const selStart = rowSel
                                ? Math.min(rowSel.anchorIdx, rowSel.headIdx)
                                : null
                            const selEnd = rowSel
                                ? Math.max(rowSel.anchorIdx, rowSel.headIdx)
                                : null
                            return (
                                <div
                                    key={empId}
                                    className="portal-calendar__gantt-row"
                                    style={{ gridTemplateColumns }}
                                >
                                    {/* Employee column — the whole cell links to the profile */}
                                    <div
                                        className="portal-calendar__employee-cell"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigateTo(`/people/${empId}?tab=time-away`)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                navigateTo(`/people/${empId}?tab=time-away`)
                                            }
                                        }}
                                    >
                                        <div className="portal-calendar__avatar">
                                            {employee.avatarUrl ? (
                                                <img src={employee.avatarUrl} alt="" />
                                            ) : (
                                                getInitials(employee.firstName, employee.lastName)
                                            )}
                                        </div>
                                        <div className="portal-calendar__employee-meta">
                                            <span className="portal-calendar__employee-name">
                                                {employee.firstName} {employee.lastName}
                                            </span>
                                            {(() => {
                                                const capacity = showCapacity
                                                    ? resolveCapacityLabel({
                                                          collaborationType:
                                                              employee.collaborationType,
                                                          fte:
                                                              activeFteByEmployee.get(empId) ??
                                                              null,
                                                      })
                                                    : null
                                                if (!employee.companySlugs && !capacity) return null
                                                return (
                                                    <span className="portal-calendar__employee-sub">
                                                        {employee.companySlugs && (
                                                            <span className="portal-calendar__employee-company">
                                                                {formatCompanies(
                                                                    employee.companySlugs,
                                                                )}
                                                            </span>
                                                        )}
                                                        {capacity && (
                                                            <>
                                                                {employee.companySlugs && (
                                                                    <span className="portal-calendar__employee-sep">
                                                                        ·
                                                                    </span>
                                                                )}
                                                                <span className="portal-calendar__employee-capacity">
                                                                    {capacity.kind === 'flex'
                                                                        ? tOff.capacityFlex(lang)
                                                                        : // Always a dot decimal (0.8), even in et.
                                                                          formatFte(
                                                                              capacity.fte,
                                                                              'en',
                                                                          )}
                                                                </span>
                                                            </>
                                                        )}
                                                    </span>
                                                )
                                            })()}
                                        </div>
                                    </div>

                                    {/* Day cells (background grid) */}
                                    {days.map((day, idx) => {
                                        const dateStr = formatDateStr(day)
                                        const dayOfWeek = day.getDay()
                                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
                                        const isHoliday = holidaySet.has(dateStr)
                                        const isToday = dateStr === todayDate
                                        const wpStatus = isWeekend
                                            ? 'full'
                                            : workingDayStatus(empId, dateStr, dayOfWeek)

                                        return (
                                            <div
                                                key={dateStr}
                                                className={classNames(
                                                    'portal-calendar__day-cell',
                                                    isWeekend && 'is-weekend',
                                                    isHoliday && 'is-holiday',
                                                    isToday && 'is-today',
                                                    showFlex &&
                                                        wpStatus === 'off' &&
                                                        'is-non-working',
                                                    showFlex &&
                                                        wpStatus === 'partial' &&
                                                        'is-partial-working',
                                                    idx === hoveredColIdx && 'is-col-hovered',
                                                    rowBookable && 'is-bookable',
                                                    selStart !== null &&
                                                        selEnd !== null &&
                                                        idx >= selStart &&
                                                        idx <= selEnd &&
                                                        'is-selecting',
                                                )}
                                                style={{ gridColumn: idx + 2 }}
                                                onMouseDown={
                                                    rowBookable
                                                        ? (e) => {
                                                              if (e.button !== 0) return
                                                              // Keep the browser from starting a text selection.
                                                              e.preventDefault()
                                                              setDragSel({
                                                                  empId,
                                                                  anchorIdx: idx,
                                                                  headIdx: idx,
                                                              })
                                                          }
                                                        : undefined
                                                }
                                                onMouseEnter={() => {
                                                    setHoveredColIdx(idx)
                                                    // The drag head tracks the column even when the
                                                    // pointer drifts onto another row.
                                                    setDragSel((sel) =>
                                                        sel && sel.headIdx !== idx
                                                            ? { ...sel, headIdx: idx }
                                                            : sel,
                                                    )
                                                }}
                                            >
                                                {isHoliday && (
                                                    <span
                                                        className="portal-calendar__holiday-dot"
                                                        onMouseEnter={(e) =>
                                                            showTip(
                                                                e.currentTarget,
                                                                renderHolidayTip(dateStr),
                                                            )
                                                        }
                                                        onMouseLeave={hideTip}
                                                    />
                                                )}
                                            </div>
                                        )
                                    })}

                                    {/* Leave bars overlaid */}
                                    {employee.entries.map((entry) => {
                                        const { style, isSingleDay } = getBarLayout(entry)
                                        const hexStyle = hexBadgeStyle(entry.badgeType)
                                        return (
                                            <div
                                                key={entry.id}
                                                className={classNames(
                                                    'portal-calendar__leave-bar',
                                                    !hexStyle &&
                                                        `portal-calendar__leave-bar--${entry.badgeType}`,
                                                    entry.status === 'PENDING' &&
                                                        'portal-calendar__leave-bar--pending',
                                                    isSingleDay &&
                                                        'portal-calendar__leave-bar--single',
                                                )}
                                                style={hexStyle ? { ...style, ...hexStyle } : style}
                                                aria-label={entry.leaveTypeName}
                                                onClick={() => setSelectedEntry(entry)}
                                                onMouseEnter={(e) =>
                                                    showTip(e.currentTarget, renderLeaveTip(entry))
                                                }
                                                onMouseLeave={hideTip}
                                                onFocus={(e) =>
                                                    showTip(e.currentTarget, renderLeaveTip(entry))
                                                }
                                                onBlur={hideTip}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        setSelectedEntry(entry)
                                                    }
                                                }}
                                            >
                                                <span className="portal-calendar__leave-bar-emoji">
                                                    {getLeaveBadgeEmoji(entry.badgeType)}
                                                </span>
                                                {!isSingleDay && (
                                                    <span className="portal-calendar__leave-bar-label">
                                                        {entry.leaveTypeName}
                                                    </span>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })
                    )}

                    {/* Dashed dividers marking each month change across the grid.
                        Positioned at the left edge of the first-of-month column;
                        the fixed name/arrow columns are excluded from the 1fr span. */}
                    {monthBoundaryIdxs.map((idx) => (
                        <div
                            key={`month-divider-${idx}`}
                            className="portal-calendar__month-divider"
                            style={{
                                left: `calc(${nameColWidth}px + ${idx} * (100% - ${
                                    nameColWidth + ARROW_COL_WIDTH
                                }px) / ${days.length})`,
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Holidays in the visible window */}
            {windowHolidays.length > 0 && (
                <div className="portal-calendar__holidays">
                    <h3 className="portal-calendar__holidays-title">{tOff.holidays.title(lang)}</h3>
                    <div className="portal-calendar__holidays-list">
                        {windowHolidays.map((h) => (
                            <span key={h.id} className="portal-calendar__holiday-chip">
                                <span className="portal-calendar__holiday-chip-dot" />
                                <span className="portal-calendar__holiday-date">
                                    {new Date(h.date + 'T00:00:00').getDate()}.
                                </span>
                                {localizeHolidayName(h.date, h.name, lang)}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Leave Detail Slide-over */}
            {selectedEntry && (
                <div
                    className="portal-calendar__slideover-overlay"
                    onClick={() => setSelectedEntry(null)}
                >
                    <div
                        className="portal-calendar__slideover"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="portal-calendar__slideover-close"
                            onClick={() => setSelectedEntry(null)}
                            aria-label={t.common.close(lang)}
                        >
                            <CloseIcon size={18} />
                        </button>

                        <div className="portal-calendar__slideover-header">
                            <div className="portal-calendar__avatar portal-calendar__avatar--lg">
                                {selectedEntry.avatarUrl ? (
                                    <img src={selectedEntry.avatarUrl} alt="" />
                                ) : (
                                    getInitials(selectedEntry.firstName, selectedEntry.lastName)
                                )}
                            </div>
                            <div>
                                <div className="portal-calendar__slideover-name">
                                    {selectedEntry.firstName} {selectedEntry.lastName}
                                </div>
                                <div className="portal-calendar__slideover-type">
                                    {selectedEntry.leaveTypeName}
                                </div>
                            </div>
                        </div>

                        <div className="portal-calendar__slideover-body">
                            <div className="portal-calendar__slideover-field">
                                <span className="portal-calendar__slideover-label">
                                    {tOff.startDate(lang)}
                                </span>
                                <span>{formatDisplayDate(selectedEntry.start_date)}</span>
                            </div>
                            <div className="portal-calendar__slideover-field">
                                <span className="portal-calendar__slideover-label">
                                    {tOff.endDate(lang)}
                                </span>
                                <span>{formatDisplayDate(selectedEntry.end_date)}</span>
                            </div>
                            <div className="portal-calendar__slideover-field">
                                <span className="portal-calendar__slideover-label">
                                    {t.portal.inline.businessDays(lang)}
                                </span>
                                <span>{selectedEntry.business_days}</span>
                            </div>
                            <div className="portal-calendar__slideover-field">
                                <span className="portal-calendar__slideover-label">
                                    {t.portal.inline.statusLabel(lang)}
                                </span>
                                <span
                                    className={classNames(
                                        'portal-calendar__slideover-status',
                                        `portal-calendar__slideover-status--${selectedEntry.status.toLowerCase()}`,
                                    )}
                                >
                                    {tOff.status[
                                        selectedEntry.status.toLowerCase() as
                                            | 'approved'
                                            | 'pending'
                                            | 'rejected'
                                    ](lang)}
                                </span>
                            </div>
                            {selectedEntry.teamNames && (
                                <div className="portal-calendar__slideover-field">
                                    <span className="portal-calendar__slideover-label">
                                        {t.portal.inline.team(lang)}
                                    </span>
                                    <span>{selectedEntry.teamNames}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Shared hover tooltip — fixed-position so it escapes gantt overflow */}
            {hoverTip && (
                <div
                    className="portal-calendar__hover-tip"
                    style={{
                        position: 'fixed',
                        left: hoverTip.rect.left + hoverTip.rect.width / 2,
                        top: hoverTip.rect.top,
                    }}
                    role="tooltip"
                >
                    {hoverTip.content}
                </div>
            )}
        </PageShell>
    )
}
